import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import sessionManagementService from '../services/sessionManagementService';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const startSessionTracking = async (userId) => {
    // Ensure we only ever have one heartbeat interval running
    sessionManagementService.stopHeartbeat();

    const existingToken = localStorage.getItem('session_token');

    if (!existingToken) {
      const sessionResult = await sessionManagementService.createSession(userId);
      
      if (!sessionResult.success) {
        await supabase?.auth?.signOut();
        throw new Error(sessionResult.message || 'Maximum number of active sessions reached.');
      }
    }

    sessionManagementService.startHeartbeat();
  };

  useEffect(() => {
    console.log('AuthProvider: Initializing');
    
    const init = async () => {
      try {
        const { data: { session } } = await supabase?.auth?.getSession();
        console.log('AuthProvider: Initial session', session ? 'Found' : 'None');
        
        setUser(session?.user ?? null);
        
        if (session?.user) {
          await Promise.all([
            loadUserProfile(session.user.id),
            startSessionTracking(session.user.id)
          ]);
        } else {
          setLoading(false);
        }
      } catch (err) {
        console.error('AuthProvider: Error getting session', err);
        setLoading(false);
      }
    };

    init();

    // Listen for auth changes (MUST be synchronous)
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
      console.log('AuthProvider: Auth state changed', event);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        loadUserProfile(session?.user?.id);
        startSessionTracking(session.user.id).catch(err => {
          console.error('AuthProvider: Session tracking error on auth change', err);
        });
      } else {
        sessionManagementService.stopHeartbeat();
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription?.unsubscribe();
  }, []);

  const loadUserProfile = async (userId) => {
    try {
      // Prefer maybeSingle to avoid 406 when no row exists yet
      let { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.maybeSingle();

      // Fallback: try to find by email if id-based lookup fails (e.g., Whop-only account created before auth signup)
      if ((!data || error) && user?.email) {
        const byEmail = await supabase?.from('user_profiles')?.select('*')?.eq('email', user.email)?.maybeSingle();
        if (!byEmail.error && byEmail.data) {
          data = byEmail.data;
          error = null;
        }
      }

      // Check localStorage for onboarding flag as fallback
      const localOnboardingDone = typeof window !== 'undefined' && localStorage.getItem('ugc_intro_done') === 'true';
      
      // If still no profile, create a minimal one to unblock the session
      if ((!data || error) && user) {
        const insertPayload = {
          id: userId,
          email: user.email,
          full_name: user.user_metadata?.full_name || user.email?.split('@')?.[0] || 'New User',
          onboarding_completed: false,
          subscription_status: 'inactive',
          subscription_plan: 'free',
          subscription_tier: 'free',
          ugc_status: 'inactive',
          ugc_plan: 'free',
          ugc_tier: 'free',
          ugc_onboarding_completed: localOnboardingDone,
        };

        const { data: created, error: insertError } = await supabase
          ?.from('user_profiles')
          ?.insert(insertPayload)
          ?.select()
          ?.maybeSingle();

        if (!insertError && created) {
          data = created;
          error = null;
        } else if (insertError) {
          throw insertError;
        }
      }

      if (error) throw error;
      
      // Fallback: if profile doesn't have onboarding flag but localStorage does, use localStorage
      if (data && !data.ugc_onboarding_completed && typeof window !== 'undefined') {
        const localOnboardingDone = localStorage.getItem('ugc_intro_done') === 'true';
        if (localOnboardingDone) {
          data.ugc_onboarding_completed = true;
        }
      }
      
      setProfile(data);
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase?.auth?.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    
    // Create session tracking
    if (data?.user) {
      await startSessionTracking(data.user.id);
    }
    
    return data;
  };

  const signUp = async (email, password, fullName, additionalData = {}) => {
    const { data, error } = await supabase?.auth?.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
          role: 'member',
          ...additionalData
        }
      }
    });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    // End session tracking
    await sessionManagementService.endSession();
    sessionManagementService.stopHeartbeat();
    
    const { error } = await supabase?.auth?.signOut();
    if (error) throw error;
  };

  const updateProfile = async (updates) => {
    if (!user) throw new Error('No user logged in');

    const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single();

    if (error) throw error;
    setProfile(data);
    return data;
  };

  const refreshProfile = async () => {
    if (!user) return;
    await loadUserProfile(user.id);
  };

  const value = {
    user,
    profile,
    loading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    refreshProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
